import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Phone, User, Mail, MessageSquare, Loader2, CheckCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CallbackRequestModal = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    nom: "",
    telephone: "",
    email: "",
    message: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nom || !formData.telephone) {
      toast.error("Veuillez remplir les champs obligatoires");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/callback-requests`, formData);
      setSuccess(true);
      toast.success("Demande envoyée ! Nous vous rappelons rapidement.");
      
      // Reset form after 2 seconds and close
      setTimeout(() => {
        setFormData({ nom: "", telephone: "", email: "", message: "" });
        setSuccess(false);
        onClose();
      }, 2000);
    } catch (error) {
      toast.error("Erreur lors de l'envoi. Veuillez réessayer.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (open) => {
    if (!open) {
      setSuccess(false);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="callback-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#000091]">
            <Phone className="w-5 h-5" />
            Demande de rappel
          </DialogTitle>
          <DialogDescription>
            Un expert vous rappellera dans les plus brefs délais pour vous accompagner 
            dans vos démarches d'immatriculation hors UE.
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="py-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-[#0F172A] mb-2">Demande envoyée !</h3>
            <p className="text-slate-600">
              Nous vous rappelons très rapidement.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nom" className="flex items-center gap-2">
                <User className="w-4 h-4 text-slate-500" />
                Nom complet <span className="text-red-500">*</span>
              </Label>
              <Input
                id="nom"
                name="nom"
                value={formData.nom}
                onChange={handleChange}
                placeholder="Votre nom et prénom"
                required
                data-testid="callback-nom"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="telephone" className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-slate-500" />
                Téléphone <span className="text-red-500">*</span>
              </Label>
              <Input
                id="telephone"
                name="telephone"
                type="tel"
                value={formData.telephone}
                onChange={handleChange}
                placeholder="06 12 34 56 78"
                required
                data-testid="callback-telephone"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-slate-500" />
                Email (optionnel)
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="votre@email.com"
                data-testid="callback-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="message" className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-slate-500" />
                Message (optionnel)
              </Label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Décrivez brièvement votre situation (pays d'origine du véhicule, type de véhicule...)"
                rows={3}
                data-testid="callback-message"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1"
                disabled={loading}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-[#000091] hover:bg-[#000091]/90"
                disabled={loading}
                data-testid="callback-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Envoi...
                  </>
                ) : (
                  "Envoyer ma demande"
                )}
              </Button>
            </div>

            <p className="text-xs text-slate-500 text-center">
              En soumettant ce formulaire, vous acceptez d'être contacté par notre équipe.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CallbackRequestModal;
